import { toPng } from "html-to-image";

const STYLE_PROPS = [
  "overflow",
  "overflowX",
  "overflowY",
  "width",
  "minWidth",
  "maxWidth",
  "flexShrink",
  "visibility",
];

function snapshotStyles(el) {
  return {
    el,
    values: Object.fromEntries(STYLE_PROPS.map((prop) => [prop, el.style[prop]])),
  };
}

function restoreStyles(snapshot) {
  for (const prop of STYLE_PROPS) {
    snapshot.el.style[prop] = snapshot.values[prop];
  }
}

function getFullPlotWidth(scrollEl) {
  const svg = scrollEl.querySelector("svg");
  if (svg) {
    const attrW = parseFloat(svg.getAttribute("width"));
    if (Number.isFinite(attrW) && attrW > 0) {
      return Math.ceil(attrW);
    }
    const rectW = svg.getBoundingClientRect().width;
    if (rectW > 0) {
      return Math.ceil(rectW);
    }
  }

  return Math.max(scrollEl.scrollWidth, scrollEl.clientWidth);
}

/**
 * Expand horizontally scrollable plot areas so the full chart is visible before
 * capture (wide charts export as landscape-friendly images for PDF use).
 */
function prepareWideChartsForCapture(root) {
  const restores = [];
  const scrollPlots = root.querySelectorAll("[data-chart-scroll-plot]");

  for (const scrollEl of scrollPlots) {
    const fullW = getFullPlotWidth(scrollEl);
    if (fullW <= scrollEl.clientWidth + 1) continue;

    restores.push(snapshotStyles(scrollEl));
    scrollEl.style.overflow = "visible";
    scrollEl.style.overflowX = "visible";
    scrollEl.style.overflowY = "visible";
    scrollEl.style.width = `${fullW}px`;
    scrollEl.style.minWidth = `${fullW}px`;
    scrollEl.style.maxWidth = "none";

    const svg = scrollEl.querySelector("svg");
    if (svg) {
      restores.push(snapshotStyles(svg));
      svg.style.width = `${fullW}px`;
      svg.style.minWidth = `${fullW}px`;
      svg.style.maxWidth = "none";
      svg.style.flexShrink = "0";
    }

    let ancestor = scrollEl.parentElement;
    while (ancestor && ancestor !== root) {
      restores.push(snapshotStyles(ancestor));
      ancestor.style.overflow = "visible";
      ancestor.style.overflowX = "visible";
      ancestor.style.minWidth = `${fullW}px`;
      ancestor.style.maxWidth = "none";
      ancestor = ancestor.parentElement;
    }
  }

  restores.push(snapshotStyles(root));
  root.style.overflow = "visible";
  root.style.maxWidth = "none";

  return restores;
}

function settleLayout() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function triggerDownload(dataUrl, filename) {
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

const SVG_PRESENTATION_ATTRS = [
  ["stroke", "stroke"],
  ["stroke-width", "strokeWidth"],
  ["stroke-dasharray", "strokeDasharray"],
  ["stroke-linecap", "strokeLinecap"],
  ["fill", "fill"],
  ["opacity", "opacity"],
];

const SVG_TEXT_ATTRS = [
  ["font-size", "fontSize"],
  ["font-family", "fontFamily"],
  ["font-weight", "fontWeight"],
];

const SVG_INLINE_SELECTOR =
  "svg line, svg circle, svg rect, svg path, svg text, svg tspan, svg polyline";

/**
 * Lock each SVG to its on-screen pixel size so viewBox-scaled charts (w-full)
 * export with the same proportions and label sizing as the live dashboard.
 */
function normalizeSvgDimensionsForExport(root) {
  const snapshots = [];

  for (const svg of root.querySelectorAll("svg")) {
    const rect = svg.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) continue;

    const width = Math.ceil(rect.width);
    const height = Math.ceil(rect.height);
    const before = {
      width: svg.getAttribute("width"),
      height: svg.getAttribute("height"),
      styleWidth: svg.style.width,
      styleHeight: svg.style.height,
    };

    svg.setAttribute("width", String(width));
    svg.setAttribute("height", String(height));
    svg.style.width = `${width}px`;
    svg.style.height = `${height}px`;

    snapshots.push({ svg, before });
  }

  return snapshots;
}

function restoreSvgDimensionSnapshots(snapshots) {
  for (const { svg, before } of snapshots) {
    if (before.width == null) svg.removeAttribute("width");
    else svg.setAttribute("width", before.width);

    if (before.height == null) svg.removeAttribute("height");
    else svg.setAttribute("height", before.height);

    svg.style.width = before.styleWidth;
    svg.style.height = before.styleHeight;
  }
}

/**
 * html-to-image does not reliably apply Vue scoped CSS to SVG children. Copy
 * computed presentation styles onto attributes so grid lines, dashes, fills,
 * and text fonts survive PNG export.
 */
function embedSvgStylesForExport(root) {
  const snapshots = [];

  for (const el of root.querySelectorAll(SVG_INLINE_SELECTOR)) {
    const computed = getComputedStyle(el);
    const before = {};
    const setAttrs = [];
    const tag = el.tagName.toLowerCase();
    const attrSets =
      tag === "text" || tag === "tspan"
        ? [...SVG_PRESENTATION_ATTRS, ...SVG_TEXT_ATTRS]
        : SVG_PRESENTATION_ATTRS;

    for (const [attr, prop] of attrSets) {
      const value = computed[prop];
      if (!value || value === "none") continue;
      if (prop === "strokeDasharray" && value === "none") continue;

      before[attr] = el.getAttribute(attr);
      setAttrs.push(attr);
      el.setAttribute(attr, value);
    }

    if (setAttrs.length > 0) {
      snapshots.push({ el, before, setAttrs });
    }
  }

  return snapshots;
}

function restoreSvgStyleSnapshots(snapshots) {
  for (const { el, before, setAttrs } of snapshots) {
    for (const attr of setAttrs) {
      const value = before[attr];
      if (value == null) el.removeAttribute(attr);
      else el.setAttribute(attr, value);
    }
  }
}

/**
 * Capture a chart card DOM node as PNG and download it.
 */
export async function downloadChartCardAsPng(
  root,
  { filename = "chart.png", pixelRatio = 2 } = {},
) {
  if (!root) return;

  const restores = prepareWideChartsForCapture(root);

  await settleLayout();

  const targetWidth = Math.max(root.scrollWidth, root.clientWidth);
  if (targetWidth > root.clientWidth) {
    root.style.width = `${targetWidth}px`;
    root.style.minWidth = `${targetWidth}px`;
  }

  await settleLayout();

  const hideTargets = [
    ...root.querySelectorAll(".chart-image-download, .work-area-filter-slot"),
  ];
  for (const el of hideTargets) {
    restores.push(snapshotStyles(el));
    el.style.visibility = "hidden";
  }

  const svgDimensionSnapshots = normalizeSvgDimensionsForExport(root);
  await settleLayout();

  const svgSnapshots = embedSvgStylesForExport(root);

  try {
    const dataUrl = await toPng(root, {
      pixelRatio,
      cacheBust: true,
      backgroundColor: "#ffffff",
    });
    triggerDownload(dataUrl, filename);
  } finally {
    restoreSvgStyleSnapshots(svgSnapshots);
    restoreSvgDimensionSnapshots(svgDimensionSnapshots);
    for (const snapshot of restores.reverse()) {
      restoreStyles(snapshot);
    }
  }
}
